import { formatInTimeZone } from "date-fns-tz";

const APP_TIMEZONE = "Europe/Sarajevo";

type DateInput = string | number | Date;

type BsDateOptions = Pick<Intl.DateTimeFormatOptions, "day" | "month" | "year" | "weekday" | "timeZone">;

type BsTimeOptions = Pick<
  Intl.DateTimeFormatOptions,
  "hour" | "minute" | "second" | "timeZone" | "hour12"
>;

type BsDateTimeOptions = BsDateOptions &
  BsTimeOptions &
  Pick<Intl.DateTimeFormatOptions, "dateStyle" | "timeStyle">;

type DateParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
};

export function formatDate(dateStr: string): string {
  return formatDateWithOptionsBs(dateStr, {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: APP_TIMEZONE,
  });
}

export function formatDateShort(dateStr: string): string {
  return formatDateWithOptionsBs(dateStr, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: APP_TIMEZONE,
  });
}

export function formatTimeShort(dateStr: string): string {
  return formatTimeWithOptionsBs(dateStr, {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: APP_TIMEZONE,
  });
}

export function formatCurrency(value: number | null | undefined, currency = "KM"): string {
  return `${(value ?? 0).toFixed(2)} ${currency}`;
}

const BS_MONTHS = [
  "januar",
  "februar",
  "mart",
  "april",
  "maj",
  "juni",
  "juli",
  "august",
  "septembar",
  "oktobar",
  "novembar",
  "decembar",
] as const;

const BS_MONTHS_SHORT = [
  "jan",
  "feb",
  "mar",
  "apr",
  "maj",
  "jun",
  "jul",
  "aug",
  "sep",
  "okt",
  "nov",
  "dec",
] as const;

const BS_WEEKDAYS = [
  "nedjelja",
  "ponedjeljak",
  "utorak",
  "srijeda",
  "četvrtak",
  "petak",
  "subota",
] as const;

const BS_WEEKDAYS_SHORT = ["ned", "pon", "uto", "sri", "čet", "pet", "sub"] as const;

function getDateParts(input: DateInput, timeZone?: string): DateParts {
  const date = input instanceof Date ? input : new Date(input);

  if (!timeZone) {
    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
      hour: date.getHours(),
      minute: date.getMinutes(),
      second: date.getSeconds(),
    };
  }

  const [datePart, timePart] = formatInTimeZone(date, timeZone, "yyyy-MM-dd'T'HH:mm:ss").split("T") as [
    string,
    string,
  ];
  const [year, month, day] = datePart.split("-").map(Number) as [number, number, number];
  const [hour, minute, second] = timePart.split(":").map(Number) as [number, number, number];
  return { year, month, day, hour, minute, second };
}

function weekdayIndex(parts: DateParts): number {
  return new Date(Date.UTC(parts.year, parts.month - 1, parts.day)).getUTCDay();
}

function formatDay(day: number, style: BsDateOptions["day"]): string {
  return style === "2-digit" ? String(day).padStart(2, "0") : String(day);
}

function formatMonth(month: number, style: BsDateOptions["month"]): string {
  if (style === "long") return BS_MONTHS[month - 1] ?? String(month);
  if (style === "short") return BS_MONTHS_SHORT[month - 1] ?? String(month);
  if (style === "2-digit") return String(month).padStart(2, "0");
  return String(month);
}

function formatYear(year: number, style: BsDateOptions["year"]): string {
  return style === "2-digit" ? String(year).slice(-2) : String(year);
}

function hasDateFields(options: BsDateOptions): boolean {
  return Boolean(options.weekday || options.day || options.month || options.year);
}

function hasTimeFields(options: BsTimeOptions): boolean {
  return Boolean(options.hour || options.minute || options.second);
}

export function formatDateWithOptionsBs(input: DateInput, options: BsDateOptions = {}): string {
  const parts = getDateParts(input, options.timeZone);
  const pieces: string[] = [];

  if (options.day) pieces.push(`${formatDay(parts.day, options.day)}.`);
  if (options.month) pieces.push(formatMonth(parts.month, options.month));
  if (options.year) pieces.push(`${formatYear(parts.year, options.year)}.`);

  const datePart = pieces.join(" ");
  if (!options.weekday) return datePart;

  const weekday =
    options.weekday === "long"
      ? BS_WEEKDAYS[weekdayIndex(parts)]
      : BS_WEEKDAYS_SHORT[weekdayIndex(parts)];

  return datePart ? `${weekday}, ${datePart}` : (weekday ?? "");
}

export function formatTimeWithOptionsBs(input: DateInput, options: BsTimeOptions = {}): string {
  const parts = getDateParts(input, options.timeZone);
  const hour = String(parts.hour).padStart(options.hour === "numeric" ? 1 : 2, "0");
  const minute = String(parts.minute).padStart(2, "0");
  const second = String(parts.second).padStart(2, "0");

  if (options.second) return `${hour}:${minute}:${second}`;
  if (options.hour || options.minute) return `${hour}:${minute}`;
  return "";
}

export function formatDateTimeWithOptionsBs(input: DateInput, options: BsDateTimeOptions = {}): string {
  if (options.dateStyle || options.timeStyle) {
    const dateOptions: BsDateOptions =
      options.dateStyle === "short"
        ? { day: "numeric", month: "numeric", year: "numeric", timeZone: options.timeZone }
        : { day: "numeric", month: "short", year: "numeric", timeZone: options.timeZone };
    const timeOptions: BsTimeOptions =
      options.timeStyle === "medium" || options.timeStyle === "long" || options.timeStyle === "full"
        ? { hour: "2-digit", minute: "2-digit", second: "2-digit", timeZone: options.timeZone }
        : { hour: "2-digit", minute: "2-digit", timeZone: options.timeZone };

    const date = options.dateStyle ? formatDateWithOptionsBs(input, dateOptions) : "";
    const time = options.timeStyle ? formatTimeWithOptionsBs(input, timeOptions) : "";
    if (date && time) return `${date} u ${time}`;
    return date || time;
  }

  const date = hasDateFields(options) ? formatDateWithOptionsBs(input, options) : "";
  const time = hasTimeFields(options) ? formatTimeWithOptionsBs(input, options) : "";
  if (date && time) return `${date}, ${time}`;
  return date || time;
}

// Format ISO date string as "27. april 2026. u 20:20" (Bosnian).
// Use this instead of toLocaleString("bs-BA", ...) which Bun's Intl
// renders as "2026 M04 27 20:20" (CLDR placeholder fallback).
export function formatDateTimeBs(dateStr: string): string {
  return `${formatDateWithOptionsBs(dateStr, {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: APP_TIMEZONE,
  })} u ${formatTimeWithOptionsBs(dateStr, {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: APP_TIMEZONE,
  })}`;
}
